import React, { useState } from "react";
import { FONT, COLOR } from "../lib/tokens";

// Renders ONLY when the orchestrator made at least one real correction
// to the recipe. The badge is a receipt of what we adjusted — not a
// permanent trust sticker on every recipe view. Clean recipes show
// no badge (absence is the signal that nothing needed adjustment).
//
// Default state is expanded so the corrections are immediately
// visible. Tap the pill row to collapse when the user has read them.
//
// Props:
//   diagnostics  — output of runRecipeTechniqueChecks().diagnostics
//   variant      — "compact" (10px / 4px padding) for in-flow recipe
//                  drafts, "default" (11px / 6px padding) for the
//                  recipe overview header.
export default function TechniqueCheckBadge({ diagnostics, variant = "default" }) {
  const fixes = Array.isArray(diagnostics?.autoFixed) ? diagnostics.autoFixed : [];
  const [open, setOpen] = useState(true);
  if (fixes.length === 0) return null;

  const isCompact = variant === "compact";
  const pillFs = isCompact ? 10 : 11;
  const pillPad = isCompact ? "4px 10px" : "6px 12px";
  const bodyFs = isCompact ? 12 : 13;
  const gapTop = isCompact ? 8 : 12;
  const countLabel = fixes.length === 1 ? "1 FIX" : `${fixes.length} FIXES`;

  return (
    <div style={{ marginTop: gapTop, display: "block" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="technique-check-list"
        className="mcm-focusable"
        style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          padding: pillPad,
          background: `${COLOR.leaf}1a`,
          border: `1px solid ${COLOR.leaf}66`,
          borderRadius: 999,
          fontFamily: FONT.mono,
          fontSize: pillFs, fontWeight: 700, letterSpacing: "0.14em",
          color: COLOR.leaf, cursor: "pointer",
        }}
      >
        <span>✓</span>
        <span>TECHNIQUE CHECK · {countLabel}</span>
        <span style={{
          fontSize: pillFs - 1, opacity: 0.7,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 140ms ease",
          marginLeft: 2, lineHeight: 1,
        }}>▾</span>
      </button>
      {open && (
        <ul
          id="technique-check-list"
          style={{
            margin: "8px 0 0 0", padding: 0, listStyle: "none",
            display: "flex", flexDirection: "column", gap: 6,
          }}
        >
          {fixes.map((fix, i) => (
            <li
              key={`${fix.source || "fix"}-${i}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                padding: "8px 10px",
                background: `${COLOR.leaf}0d`,
                border: `1px solid ${COLOR.leaf}26`,
                borderRadius: 8,
                fontFamily: FONT.forma, fontSize: bodyFs, lineHeight: 1.35,
                color: COLOR.ink,
              }}
            >
              <span style={{
                fontFamily: FONT.mono, fontSize: bodyFs - 2, fontWeight: 700,
                color: COLOR.leaf, letterSpacing: "0.1em",
                paddingTop: 1, minWidth: 56, textAlign: "left",
                textTransform: "uppercase",
              }}>
                {sourceLabel(fix.source)}
              </span>
              <span style={{ flex: 1 }}>{fix.summary || "Adjusted a step for accuracy."}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function sourceLabel(source) {
  switch (source) {
    case "meat":    return "MEAT";
    case "frying":  return "HEAT";
    case "pasta":   return "PASTA";
    case "baking":  return "BAKING";
    default:        return "FIX";
  }
}
